import ElementController from '../base-controllers/ElementController';
import initializer from '../initializer';
import {
  dataURLtoBlob,
  getData,
  isElement,
  removeData,
  setData,
} from '../utils/dom';
import { isUndefined } from '../utils/js';
import { httpGetJSON, httpPost, JSON_MIME_TYPE } from '../utils/http';
import {
  FORM_CONTROLLER_ID,
  FORM_EVENT,
  FORM_SUBMITTING_DATA_ATTR,
} from '../../constants';
import { SubmitEvent } from '../types/event';

const AJAX_DATA_ATTR = 'ajax';
const ERROR_TEXT_DATA_ATTR = 'error-text';
const FORMSET_PREFIX = '__prefix__';
const GROUP_NAME_DATA_ATTR = 'group-name';
const GROUP_TYPE_DATA_ATTR = 'group-type';
const IS_GROUP_FIELD_DATA_ATTR = 'is-group-field';
const MESSAGES_VAR_DATA_ATTR = 'messages-var';
const SUBMITTING_TEXT_DATA_ATTR = 'submitting-text';
const VALIDATE_DATA_ATTR = 'validate';
const WARN_IF_CHANGED_DATA_ATTR = 'warn-if-changed';

const WARNING_ICON =
  '<svg viewBox="0 0 16 16" width="16px" height="16px" xmlns="http://www.w3.org/2000/svg" focusable="false" tabindex="-1"><path d="M8 1.4l6.7 13.4H1.3L8 1.4zM8 0c-.3 0-.7.2-1 .7L.2 14.3c-.5.9-.1 1.7 1 1.7h13.6c1.1 0 1.5-.8 1-1.7L9 .7C8.7.2 8.3 0 8 0z"></path><path d="M9 13c0 .6-.4 1-1 1s-1-.4-1-1 .4-1 1-1 1 .4 1 1zm-1-2c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1s1 .4 1 1v3c0 .6-.4 1-1 1z"></path></svg>';

// Set no-break space if removing. Screen readers can try to read elements only
// containing whitespace, so hide it.
const NO_BREAK_SPACE_HTML = '<span aria-hidden="true">&nbsp;</span>';

const FIELD_CONTAINER_SELECTOR = '.form-field';
const FORM_FIELD_ERROR_CLASS_NAME = 'form-field--error';
const FORM_DELETE_CLASS_NAME = 'delete';
const FORMSET_CONTAINER_SELECTOR = '[data-node-name~="formset-container"]';
const FORMSET_FORM_SELECTOR = '[data-node-name~="formset-form"]';
const FORMSET_FORM_REMOVE_BUTTON_SELECTOR =
  '[data-node-name~="formset-form-remove"]';
const FORMSET_FORM_DELETE_SELECTOR = '.form-field--DELETE input';
const TOTAL_FORMS_SELECTOR = 'input[name$="TOTAL_FORMS"]';

interface GlobalHandleSubmitClick {
  (selector: string, e: Event);
}

type FormInputElement = HTMLInputElement & HTMLButtonElement;

const CUSTOM_VALIDATORS = {
  value: function valueValidator(field) {
    return {
      valid: Boolean(field.value),
    };
  },
};

/**
 * General form handler that can validate and sends forms via Ajax to backend.
 *
 * Options via data attributes:
 *
 * - validate (boolean) If custom validation should run instead of the browser's
 *   built-in HTML5 validation.
 * - ajax (boolean) If the form should be sent via Ajax.
 * - messages-var (string) The global variable that hold all messages.
 * - warn-if-changed: (boolean) Whether to warn if form has been changed on page
 *   unload or if controller is being destroyed.
 *
 * Options for fields and groups:
 *
 * - error-text (string) Custom text to display when the field is invalid.
 * - is-group-field (boolean) If the field is part of a group (a group of
 *   checkboxes for example). This must be set to disable validation for a
 *   single field in the group (e.g. want a single error message for the
 *   group of checkboxes, not one for each checkbox).
 * - group-name (string) A common name for all fields in a group. Set on a
 *   group container.
 * - skip-validation (boolean) If validation should be skipped for this node.
 * - validator (string) Name of custom validator. Only built in "value"
 *   validator right now.
 * - validity (string ['valid'|'invalid']) Custom validity of any non input
 *   field nodes. Validity is either 'valid' or 'invalid'. The 'invalid' state
 *   uses error text from the error-text option.
 *
 * Events triggered on the form node:
 *
 * | Name                          | Arguments     | Cancelable | Triggered                             |
 * |-------------------------------|---------------|------------|---------------------------------------|
 * | FORM_EVENT.beforeSend         | requestParams |     ✓      | Before sending form to backend        |
 * | FORM_EVENT.enableFields       |               |     ✓      | When enabling fields                  |
 * | FORM_EVENT.errorSummary       | text          |            | On submit if there are errors         |
 * | FORM_EVENT.formsetFormAdded   | addedForm     |            | When a formset form is added          |
 * | FORM_EVENT.formsetFormRemoved |               |            | When a formset form is removed        |
 * | FORM_EVENT.response           | data, errors  |            | When response is received             |
 * | FORM_EVENT.responseHttpError  | httpError     |            | When a response returns an HTTP error |
 *
 * Form node event listeners, trigger to control the form:
 *
 * | Name                    | Arguments            | Action               |
 * |-------------------------|----------------------|----------------------|
 * | FORM_EVENT.enableFields |                      | Enable fields        |
 * | FORM_EVENT.send         | cancelableBeforeSend | Send form to backend |
 *
 * State set on the form node:
 *
 * | Name                      | Changed                 |
 * |---------------------------|-------------------------|
 * | FORM_SUBMITTING_DATA_ATTR | When form is submitting |
 */
export default class Form extends ElementController {
  static id = FORM_CONTROLLER_ID;

  static dependencies = ['PageUpdater'];

  private form: HTMLFormElement;

  private globalHandleSubmitClick: GlobalHandleSubmitClick;

  private msg: any;

  private fields: Element[];

  private fieldGroups: Element[];

  private formErrorsWrap: Element;

  private formMessageWrap: Element;

  private submittingStatus: Element;

  private errorSummary: Element;

  private submitButtons: Array<FormInputElement>;

  private pressedSubmitButton: FormInputElement | null;

  /**
   * DOM ready, init.
   */
  init() {
    this.form = this.root as HTMLFormElement;
    this.on(this.form, 'submit', this.handleSubmit);
    this.on(
      this.form,
      'blur',
      '[data-node-name~="field"]',
      this.handleFieldValidation,
      // Focus events don't bubble, so use capture
      { capture: true },
    );
    this.on(
      this.form,
      'change',
      '[data-node-name~="field"]:not([type="text"]):not([type="textarea"])',
      this.handleFieldValidation,
    );

    this.initFormNodes();

    // Keep track of when form is changed and warn user
    if (this.shouldWarnIfChanged) {
      this.initialState();
      this.on(this.form, 'change', () => this.setState({ formChanged: true }));
    }

    // If the form contains a formset add button, hook it up
    this.on(
      this.form,
      'click',
      '[data-node-name~="formset-add"]',
      this.handleAddFormToFormset,
    );

    // Hook up formset remove buttons
    this.on(
      this.form,
      'click',
      FORMSET_FORM_REMOVE_BUTTON_SELECTOR,
      this.handleRemoveFormFromFormset,
    );
    this.on(
      this.form,
      'click',
      FORMSET_FORM_DELETE_SELECTOR,
      this.handleDeleteFormFromFormset,
    );

    // Keep track of which submit button was clicked, to handle multiple ones.
    // With the capture phase this handler should run before any others.
    // This is sort of a polyfill for SubmitEvent.submitter.
    const formSubmitButtonSelector = `input[type="submit"], button[type="submit"]`;
    this.on(
      this.form,
      'click',
      formSubmitButtonSelector,
      this.handleSubmitClick.bind(this, formSubmitButtonSelector),
      { capture: true },
    );
    if (this.form.id) {
      // Save for use in destroy().
      this.globalHandleSubmitClick = this.handleSubmitClick.bind(
        this,
        this.globalFormSubmitButtonSelector,
      );
      this.onGlobal(
        'click',
        this.globalFormSubmitButtonSelector,
        this.globalHandleSubmitClick,
        { capture: true },
      );
    }

    const messagesVarName = getData(this.form, MESSAGES_VAR_DATA_ATTR);
    if (messagesVarName && window[messagesVarName]) {
      this.msg = window[messagesVarName];
    } else {
      throw new Error(
        `Missing form messages. The form must have a ${MESSAGES_VAR_DATA_ATTR} data attribute pointing to a global variable.`,
      );
    }

    // Disable the browser's validation
    if (this.shouldValidate) {
      this.form.setAttribute('novalidate', '');
    }

    // Save default formset values
    const formsetContainers = this.selectAll(FORMSET_CONTAINER_SELECTOR);
    formsetContainers.forEach((container) => {
      setData(container, 'defaultValue', container.innerHTML);
    });

    // Handle form reset
    this.on(this.form, 'reset', () => {
      // Reset default formset values
      formsetContainers.forEach((container) => {
        container.innerHTML = getData(container, 'defaultValue');
        initializer.init(container);
      });

      // Re-init fields
      this.initFieldNodes();
      this.initialState();
    });

    this.on(this.form, FORM_EVENT.enableFields, () => {
      this.enableFields();
    });

    this.on(
      this.form,
      FORM_EVENT.send,
      (event, cancelableBeforeSend = true) => {
        this.sendForm(cancelableBeforeSend);
      },
    );
  }

  /**
   * Set initial state
   */
  initialState = () => {
    this.setState({ formChanged: false });
  };

  /**
   * Handle navigating away.
   *
   * @param event - beforeunload or popstate event.
   */
  onBeforeNavigation = (event: Event | BeforeUnloadEvent) => {
    if (event.type === 'popstate') {
      // Cleanup since popstate isn't cancelable
      this.setState({ formChanged: false });
    }

    // Warn if form has changed
    if (
      !this.isSubmitting &&
      this.shouldWarnIfChanged &&
      this.state.formChanged
    ) {
      if (event.type === 'beforeunload') {
        event.preventDefault();
        event.returnValue = '';
        // eslint-disable-next-line no-alert
      } else if (!window.confirm(this.msg.unsavedWarning)) {
        event.preventDefault();
      } else {
        this.setState({ formChanged: false });
      }
    }
  };

  /**
   * Ask for user confirmation if form has been changed.
   */
  shouldDestroy(): boolean {
    // eslint-disable-next-line sonarjs/prefer-single-boolean-return
    if (
      this.shouldWarnIfChanged &&
      this.state.formChanged &&
      // eslint-disable-next-line no-alert
      !window.confirm(this.msg.closeWarning)
    ) {
      return false;
    }
    return true;
  }

  /**
   * Reset formChanged state and cleanup.
   */
  destroy() {
    if (this.globalHandleSubmitClick) {
      this.offGlobal('click', this.globalHandleSubmitClick, { capture: true });
    }
    this.initialState();
    this.enableFields();
  }

  /**
   * Cache DOM nodes for form fields.
   */
  initFieldNodes() {
    this.fields = this.selectAll('field').filter(
      (field) => !field.id.includes(FORMSET_PREFIX),
    );
    this.fieldGroups = this.selectAll('field-group');
  }

  /**
   * Cache all DOM nodes.
   */
  initFormNodes() {
    this.initFieldNodes();
    this.formErrorsWrap = this.selectSingle('form-errors-wrap') as Element;
    this.formMessageWrap = this.selectSingle('form-message-wrap') as Element;
    this.submittingStatus = this.selectSingle('submitting-status') as Element;
    this.errorSummary = this.selectSingle('error-summary') as Element;
    this.submitButtons = this.getSubmitButtons();
    this.pressedSubmitButton = null;
  }

  /**
   * Get submit buttons from form elements. This way we get all submitters that
   * refers to this form.
   *
   * @returns Submit buttons
   */
  getSubmitButtons(): Array<FormInputElement> {
    return (Array.from(this.form.elements) as Array<FormInputElement>).filter(
      (el) => this.isSubmitButton(el),
    );
  }

  /**
   * Get error field for a single form field.
   *
   * @param node - Single form field or group DOM node.
   * @returns The message DOM node.
   */
  getErrorMessageNode(node: Element): Element {
    let { id } = node;
    if (node.getAttribute('role') === 'group') {
      id = getData(node, GROUP_NAME_DATA_ATTR);
    }

    return this.selectSingle(this.form, `#${id}-error`) as Element;
  }

  /**
   * Show or remove an error related to a single field.
   *
   * @param node - Single form field or group DOM node.
   * @param [error] - Error text to display. If passed, the error state
   *   will be set, otherwise it will be removed.
   */
  showOrRemoveError(node: Element, error?: string) {
    const isError = !isUndefined(error);
    const container = node.closest(FIELD_CONTAINER_SELECTOR) as Element;
    const fields = this.selectAll(container, 'input, textarea, select');

    // Add or remove error class
    const addRemove = isError ? 'add' : 'remove';
    container.classList[addRemove](FORM_FIELD_ERROR_CLASS_NAME);

    // Add or remove field error text
    const messageNode = this.getErrorMessageNode(node);
    if (messageNode) {
      messageNode.innerHTML = error ?? NO_BREAK_SPACE_HTML;
    }

    // Set aria-invalid on all fields
    fields.forEach((field) => {
      field.setAttribute('aria-invalid', String(isError));
    });
  }

  /**
   * Empty any existing form error or success messages.
   */
  clearFormMessages() {
    this.formErrorsWrap.innerHTML = '';
    this.formMessageWrap.innerHTML = '';
  }

  /**
   * Show an error for the entire form.
   *
   * @param error - Error text to display.
   */
  showFormError(error: string) {
    this.formErrorsWrap.innerHTML = `<div class="form-errors"><p>${error}</p></div>`;
  }

  /**
   * Show error summary for the entire form.
   *
   * @param onSubmit - If this is on form submit or not.
   */
  showErrorSummary(onSubmit: boolean) {
    // Check for validity
    const errorCount = this.getErrorCount();
    if (errorCount) {
      const textTemplate =
        errorCount > 1 ? this.msg.errorSummaryPlural : this.msg.errorSummary;
      // Labels of fields or groups with errors to include as hidden text in
      // the inline error message. This should give screen readers something
      // better to go by.
      const errorLabels = this.selectAll(
        this.form,
        `.${FORM_FIELD_ERROR_CLASS_NAME} > label, .${FORM_FIELD_ERROR_CLASS_NAME} .field-group-label`,
      ).map((node) => this.getCleanLabel(node));
      const text = `${textTemplate
        .replace('{count}', errorCount)
        .replace(
          '{icon}',
          WARNING_ICON,
        )}<span class="visuallyhidden">: ${errorLabels.join(', ')}</span>`;
      this.setErrorSummary(text);
      if (onSubmit) {
        this.triggerCustomEvent(this.form, FORM_EVENT.errorSummary, text);
      }
    } else {
      this.setErrorSummary(NO_BREAK_SPACE_HTML);
    }
  }

  /**
   * Set error summary node content.
   *
   * If the error summary node is missing the `FORM_EVENT.errorSummary` event
   * should be used instead.
   *
   * @param html - The HTML string
   */
  setErrorSummary(html: string) {
    // The form may be missing the error summary node.
    if (this.errorSummary) {
      this.errorSummary.innerHTML = html;
    }
  }

  /**
   * Show an error related to a single field or group.
   *
   * @param node - Single form field or group DOM node.
   * @param error - Error text to display.
   */
  showError(node: Element, error: string) {
    this.showOrRemoveError(node, error);
  }

  /**
   * Remove an error related to a single field or group.
   *
   * @param node - Single form field or group DOM node.
   */
  removeError(node: Element) {
    this.showOrRemoveError(node);
  }

  /**
   * Check if an error is to be shown or removed in relation to a field or
   * group.
   *
   * Validation can be skipped by adding the attribute `data-skip-validation`
   * to the node.
   *
   * @param node - Single form field or group DOM node.
   * @param validatorFunc - Function that returns error if invalid.
   * @param [returnError] - If the error should be returned.
   * @returns Error message if `returnError` is true.
   */
  // eslint-disable-next-line consistent-return
  validateNode(
    node: Element,
    validatorFunc: Function,
    returnError: boolean = false,
  ): string | undefined {
    // Get possible error
    let error;
    if (!getData(node, 'skip-validation', Boolean)) {
      error = validatorFunc(node);
    }

    // If there's an error, show it
    if (error) {
      this.showError(node, error);
      return returnError ? error : undefined;
    }

    // Otherwise, remove any errors that exist
    this.removeError(node);
  }

  /**
   * Get error for a single form field, check validity.
   *
   * @param field - Single form field DOM node.
   * @returns Error message.
   */
  getFieldError = (field: HTMLInputElement): string | null => {
    const errorText = getData(field, ERROR_TEXT_DATA_ATTR);

    // Manual validity
    let validity = getData(field, 'validity');
    if (validity) {
      return validity === 'valid' ? null : errorText;
    }

    // A simple custom validator
    const validatorFuncName = getData(field, 'validator');
    validity = validatorFuncName
      ? CUSTOM_VALIDATORS[validatorFuncName](field)
      : field.validity;

    // If valid
    if (validity.valid) {
      return null;
    }

    // If field is required and empty
    if (validity.valueMissing) {
      if (field.type === 'checkbox') {
        return errorText || this.msg.valueMissingCheckbox;
      }

      if (field.type === 'radio') {
        return errorText || this.msg.valueMissingRadio;
      }

      if (field.type === 'select-multiple') {
        return errorText || this.msg.valueMissingSelectMulti;
      }

      if (field.type === 'select-one') {
        return errorText || this.msg.valueMissingSelect;
      }

      return errorText || this.msg.valueMissing;
    }

    // If not the right type
    if (validity.typeMismatch) {
      // Email
      if (field.type === 'email') {
        return errorText || this.msg.typeMismatchEmail;
      }

      // URL
      if (field.type === 'url') {
        return errorText || this.msg.typeMismatchURL;
      }
    }

    // If too short
    if (validity.tooShort) {
      return (
        errorText ||
        this.msg.tooShort
          .replace('{minLength}', field.getAttribute('minLength'))
          .replace('{length}', field.value.length)
      );
    }

    // If too long
    if (validity.tooLong) {
      return (
        errorText ||
        this.msg.tooLong
          .replace('{maxLength}', field.getAttribute('maxLength'))
          .replace('{length}', field.value.length)
      );
    }

    // If number input isn't a number
    if (validity.badInput) {
      return errorText || this.msg.badInput;
    }

    // If a number value doesn't match the step interval
    if (validity.stepMismatch) {
      return (
        errorText ||
        this.msg.stepMismatch.replace('{step}', field.getAttribute('step'))
      );
    }

    // If a number field is over the max
    if (validity.rangeOverflow) {
      return (
        errorText ||
        this.msg.rangeOverflow.replace('{max}', field.getAttribute('max'))
      );
    }

    // If a number field is below the min
    if (validity.rangeUnderflow) {
      return (
        errorText ||
        this.msg.rangeUnderflow.replace('{min}', field.getAttribute('min'))
      );
    }

    // If pattern doesn't match
    if (validity.patternMismatch) {
      return errorText || this.msg.patternMismatch;
    }

    // If all else fails, return a generic catchall error
    return errorText || this.msg.generic;
  };

  /**
   * Get error for a group of form field, check validity.
   *
   * @param group - Group container DOM node.
   * @returns Error message.
   */
  getGroupError = (group: Element): string | null => {
    const errorText = getData(group, ERROR_TEXT_DATA_ATTR);
    const groupType = getData(group, GROUP_TYPE_DATA_ATTR);

    if (groupType === 'radio') {
      // A radio group can have an empty default value
      const checkedElement = this.selectSingle(
        group,
        'input:checked',
      ) as HTMLInputElement;
      if (!checkedElement?.value) {
        return errorText || this.msg.valueMissingRadio;
      }
    }

    const checkedCount = this.selectAll(group, 'input:checked').length;
    if (groupType === 'checkbox') {
      const min = getData(group, 'min');
      const max = getData(group, 'max');

      // Has both min and max, show range error
      if (max && (checkedCount > max || checkedCount < min)) {
        return (
          errorText ||
          this.msg.countMismatch.replace('{min}', min).replace('{max}', max)
        );
      }

      // Has just min by default
      if (checkedCount < min) {
        return errorText || this.msg.tooFew.replace('{min}', min);
      }
    }

    return null;
  };

  /**
   * Get fields that have errors
   *
   * @returns Number of fields that have errors
   */
  getErrorCount(): number {
    const errorFields: Element[] = [];
    this.fields.forEach((field) => {
      if (!getData(field, IS_GROUP_FIELD_DATA_ATTR, Boolean)) {
        const error = this.validateNode(field, this.getFieldError, true);
        if (error) {
          errorFields.push(field);
        }
      }
    });

    const errorGroups: Element[] = [];
    this.fieldGroups.forEach((group) => {
      const error = this.validateNode(group, this.getGroupError, true);
      if (error) {
        errorGroups.push(group);
      }
    });

    return errorFields.length + errorGroups.length;
  }

  /**
   * Send form with either GET or POST, based on form method.
   *
   * A form can be sent later by the FORM_EVENT.send.
   *
   * @param {boolean} [cancelableBeforeSend] - If the default form submit ajax
   *   request should be cancelable (for use with FORM_EVENT.send).
   */
  sendForm = async (cancelableBeforeSend = true) => {
    let requestFunc;
    let requestParams;

    // Prepare request
    if (this.form.method.toUpperCase() === 'GET') {
      // GET request
      const actionURl = new URL(this.form.action, window.location.origin);
      this.getSerializableFields()
        .filter(
          (field) =>
            Boolean(field.value) && field.name !== 'csrfmiddlewaretoken',
        )
        .forEach((field) => {
          if (field instanceof HTMLSelectElement && field.multiple) {
            Array.from(field.selectedOptions).forEach((option) => {
              actionURl.searchParams.append(field.name, option.value);
            });
          } else {
            actionURl.searchParams.append(field.name, field.value);
          }
        });

      requestFunc = httpGetJSON;
      requestParams = [actionURl.toString()];
    } else {
      // POST request
      const formData = new FormData();

      this.getSerializableFields().forEach((field) => {
        if (field instanceof HTMLSelectElement && field.multiple) {
          Array.from(field.selectedOptions).forEach((option) => {
            formData.append(field.name, option.value);
          });
        } else {
          formData.append(field.name, this.getFieldValue(field));
        }
      });
      requestFunc = httpPost;
      requestParams = [this.form.action, formData, { Accept: JSON_MIME_TYPE }];
    }

    if (cancelableBeforeSend) {
      // Ability to cancel sending form to backend. When this is cancelled the
      // FORM_EVENT.send can be used to send form later.
      if (
        !this.triggerCancelableCustomEvent(
          this.form,
          FORM_EVENT.beforeSend,
          requestParams,
        )
      ) {
        return;
      }
    } else {
      this.triggerCustomEvent(this.form, FORM_EVENT.beforeSend, requestParams);
    }

    // Make the request
    try {
      const response = await requestFunc(...requestParams);

      this.initialState();

      this.triggerCustomEvent(
        this.form,
        FORM_EVENT.response,
        response.data,
        response.errors,
      );

      this.enableSubmitButton();
    } catch (error) {
      this.enableFields();
      this.showFormError(this.msg.unexpectedError);
      this.triggerCustomEvent(this.form, FORM_EVENT.responseHttpError, error);
      // Let potential other things run before re-throwing for Sentry to catch
      setTimeout(() => {
        throw error;
      }, 250);
    }

    // Done submitting
    removeData(this.form, FORM_SUBMITTING_DATA_ATTR);
  };

  /**
   * Get the current value for a form field.
   *
   * @param field - The field node.
   * @returns Value for regular inputs (likely a string), a File object
   *   for file inputs.
   */
  getFieldValue = (field: HTMLInputElement): string | File => {
    const { value, type, files } = field;
    let val;
    if (type === 'file') {
      [val] = files as FileList;
    } else if (
      getData(field, 'type') === 'dataURL' &&
      value.startsWith('data:')
    ) {
      const blob = dataURLtoBlob(value);
      val = new File([blob], getData(field, 'fileName'), { type: blob.type });
    } else {
      val = value;
    }

    return val;
  };

  /**
   * Runs just before the form is submitted.
   */
  preSubmittingForm = () => {
    // Clear existing form messages. This will both remove potentially obsolete
    // messages and ensure screen readers read any new messages that are the
    // same (ignored otherwise, even with aria-atomic).
    this.clearFormMessages();

    this.setSubmittingText();

    // Disable all fields while sending
    this.disableFields();
  };

  /**
   * Get form elements that should be included in a submit.
   *
   * Excludes things like buttons, unchecked checkboxes/radios, elements without
   * a name...
   *
   * @returns serializable fields
   */
  getSerializableFields(): Array<FormInputElement> {
    return (Array.from(this.form.elements) as Array<FormInputElement>).filter(
      (el: FormInputElement) => {
        // Anything without a name can't be sent.
        if (!el.name) {
          return false;
        }

        // Buttons are generally not relevant, except submit buttons with a
        // value that may be used for specific actions. Only include if it was
        // the pressed one for the current submit.
        if (this.isSubmitButton(el)) {
          return Boolean(el.value) && el === this.pressedSubmitButton;
        }

        // Exclude non-submit buttons.
        if (isElement(el, 'button')) {
          return false;
        }

        // Exclude unchecked radio/checkbox inputs.
        // TODO: https://github.com/SonarSource/eslint-plugin-sonarjs/issues/330
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (
          isElement(el, 'input', { type: ['radio', 'checkbox'] }) &&
          !(el as HTMLInputElement).checked
        ) {
          return false;
        }

        return true;
      },
    );
  }

  /**
   * Disable a single form field.
   *
   * @param field - Single form field
   */
  disableField(field: HTMLInputElement) {
    field.disabled = true;
  }

  /**
   * Enable a single form field.
   *
   * @param field - Single form field
   */
  enableField(field: HTMLInputElement) {
    field.disabled = false;
  }

  /**
   * Disable submit and fields.
   */
  disableFields() {
    this.disableSubmitButton();

    // Disabled fields are not included in a regular submit
    if (this.shouldSendViaAjax) {
      this.fields.forEach(this.disableField);
    }
  }

  /**
   * Enable submit button, and all fields.
   */
  enableFields() {
    this.enableSubmitButton();
    this.fields.forEach(this.enableField);
  }

  /**
   * Disable the form's submit button.
   */
  disableSubmitButton() {
    // Disable all submit buttons
    this.submitButtons.forEach((btn) => {
      btn.disabled = true;
    });

    // Change the text of the pressed one, start by caching the regular text.
    if (this.pressedSubmitButton) {
      setData(
        this.pressedSubmitButton,
        'original-submit-text',
        this.pressedSubmitButton.innerHTML,
      );

      // Set disabled text
      const disabledText = getData(
        this.pressedSubmitButton,
        SUBMITTING_TEXT_DATA_ATTR,
      );
      if (disabledText) {
        this.pressedSubmitButton.innerHTML = disabledText;
      }
    }
  }

  /**
   * Enable the form's submit buttons.
   */
  enableSubmitButton() {
    // Enable all buttons.
    this.submitButtons.forEach((btn) => {
      btn.disabled = false;
    });

    if (this.pressedSubmitButton) {
      // Restore the text of the pressed one.
      const originalSubmitText = getData(
        this.pressedSubmitButton,
        'original-submit-text',
      );
      if (originalSubmitText) {
        this.pressedSubmitButton.innerHTML = originalSubmitText;
      }

      // Restore focus to the submit button since it should've lost focus when
      // it got disabled.
      this.pressedSubmitButton.focus();

      this.pressedSubmitButton = null;
    }
  }

  /**
   * Display a hidden 'submitting' message for screen readers if available
   */
  setSubmittingText() {
    if (this.pressedSubmitButton) {
      const submittingText = getData(
        this.pressedSubmitButton,
        SUBMITTING_TEXT_DATA_ATTR,
      );
      if (submittingText) {
        this.submittingStatus.textContent = submittingText;
      }
    }
  }

  /**
   * Get cleaned label text.
   *
   * @param node - DOM node.
   * @returns Cleaned label
   */
  getCleanLabel(node: Element) {
    return node.textContent?.replace(/[:*]/g, '').toLowerCase().trim();
  }

  /**
   * Validate a field when the user leaves it or changes it.
   *
   * @param e - Blur or Change event.
   */
  handleFieldValidation = (e: Event) => {
    this.validateNode(e.target as Element, this.getFieldError);
    // Update error summary if form has been validated before
    // Error summary node may be missing
    if (
      this.shouldValidate &&
      this.errorSummary &&
      this.errorSummary.innerHTML !== NO_BREAK_SPACE_HTML
    ) {
      this.showErrorSummary(false);
    }
  };

  /**
   * Get formset data from button
   *
   * @param button - A formset button
   * @returns Formset data
   */
  getFormsetData(button: HTMLButtonElement): {
    container: Element;
    form: Element;
    totalInput: HTMLInputElement;
    formsetCount: number;
  } {
    const container = button.closest(FORMSET_CONTAINER_SELECTOR) as Element;
    const form = button.closest(FORMSET_FORM_SELECTOR) as Element;
    const totalInput = this.selectSingle(
      container,
      TOTAL_FORMS_SELECTOR,
    ) as HTMLInputElement;
    const formsetCount = parseInt(totalInput.value, 10);
    return {
      container,
      form,
      totalInput,
      formsetCount,
    };
  }

  /**
   * Add a group to formset when clicking the add button.
   *
   * @param e - Click event.
   */
  handleAddFormToFormset = (e: Event) => {
    const { container, totalInput, formsetCount } = this.getFormsetData(
      e.target as HTMLButtonElement,
    );
    const formset = this.selectSingle(container, 'formset') as Element;
    const formsetEmpty = this.selectSingle(
      formset,
      '.formset__empty',
    ) as Element;
    const template = this.selectSingle(
      container,
      'formset-template',
    ) as Element;
    // Insert adjacent html with the new prefix
    formsetEmpty.insertAdjacentHTML(
      'beforebegin',
      template.innerHTML.replaceAll(
        new RegExp(FORMSET_PREFIX, 'g'),
        String(formsetCount),
      ),
    );
    const forms = formset.querySelectorAll('[data-node-name~="formset-form"]');
    const addedForm = Array.from(forms).pop();
    // Add total forms count
    totalInput.value = String(formsetCount + 1);
    // Init to include the new fields added
    this.initFieldNodes();
    initializer.init(addedForm);
    this.triggerCustomEvent(this.form, FORM_EVENT.formsetFormAdded, addedForm);
  };

  /**
   * Either remove or mark a formset form for deletion.
   *
   * @param e - Click event.
   */
  handleRemoveFormFromFormset = (e: Event) => {
    e.preventDefault();
    const { form, totalInput, formsetCount } = this.getFormsetData(
      e.target as HTMLButtonElement,
    );
    // Maybe mark for deletion
    const deleteEl = this.selectSingle(form, FORMSET_FORM_DELETE_SELECTOR);
    if (deleteEl && !form.classList.contains('empty-form')) {
      (deleteEl as HTMLInputElement).checked = true;
      form.classList.add(FORM_DELETE_CLASS_NAME);
      setData(deleteEl, 'skipValidation');
    } else {
      form.remove();
      // Change total forms count
      totalInput.value = String(formsetCount - 1);
      // Init to exclude the removed fields
      this.initFieldNodes();
    }
    this.triggerCustomEvent(this.form, FORM_EVENT.formsetFormRemoved);
  };

  /**
   * Mark a formset form for deletion.
   *
   * @param e - Click event.
   */
  handleDeleteFormFromFormset = (e: Event) => {
    const { form } = this.getFormsetData(e.target as HTMLButtonElement);
    form.classList.toggle(FORM_DELETE_CLASS_NAME);
  };

  /**
   * Set the last pressed submit button.
   *
   * @param selector - The button selector
   * @param e - Click event.
   */
  handleSubmitClick = (selector: string, e: Event) => {
    // The target may be something like a span inside the button.
    this.pressedSubmitButton = (e.target as Element).closest(selector);
  };

  /**
   * Handle form submit.
   *
   * @param e - Submit event.
   */
  handleSubmit = (e: SubmitEvent) => {
    // Keep track of which submit button was clicked, to handle multiple ones.
    if (e.submitter) {
      this.pressedSubmitButton = e.submitter as FormInputElement;
    }

    if (!this.pressedSubmitButton) {
      throw new Error(
        `Can't find submitter button. The submitter must be either a descendant of the form element or must have an 'form' attribute referring to the form id.`,
      );
    }

    // We're submitting
    setData(this.form, FORM_SUBMITTING_DATA_ATTR, true);

    // Don't validate if no validation is specified
    if (this.shouldValidate) {
      this.showErrorSummary(true);
      if (this.getErrorCount()) {
        e.preventDefault();
        removeData(this.form, FORM_SUBMITTING_DATA_ATTR);
        return;
      }
    }

    this.preSubmittingForm();

    if (this.shouldSendViaAjax) {
      // Send the form to the backend
      e.preventDefault();
      this.sendForm();
    }
  };

  /**
   * Check if element is a submit button.
   *
   * @param el - The element
   * @returns If element is a submit button or not
   */
  isSubmitButton(el: Element): boolean {
    return isElement(el, ['button', 'input'], { type: 'submit' });
  }

  get globalFormSubmitButtonSelector() {
    return `input[type="submit"][form=${this.form.id}], button[type="submit"][form=${this.form.id}]`;
  }

  get shouldSendViaAjax() {
    return getData(this.form, AJAX_DATA_ATTR, Boolean);
  }

  get shouldValidate() {
    return getData(this.form, VALIDATE_DATA_ATTR, Boolean);
  }

  get shouldWarnIfChanged() {
    return getData(this.form, WARN_IF_CHANGED_DATA_ATTR, Boolean);
  }

  get isSubmitting() {
    return getData(this.form, FORM_SUBMITTING_DATA_ATTR, Boolean);
  }
}
